import { type PropsWithChildren } from 'react';
import { useMemo } from 'react';

import { NAME_ACCESSOR } from '../constants.js';
import { useColorPalette } from '../context/ColorPaletteContext.js';
import { useNameAccessor } from '../context/NameAccessorContext.js';
import { TreeMapInternalDataContext } from '../context/TreeMapInternalDataContext.js';
import { useTreeMapRawData } from '../context/TreeMapRawDataContext.js';
import { useMinMaxConfig } from '../hooks/useMinMaxConfig.js';
import { useSetState } from '../store/selectors.js';
import type { InternalTreeMapNode } from '../types/internal.js';
import { getColoredInternalCategoricalData } from '../utils/get-colored-internal-categorical-data.js';
import { getColoredInternalRangeData } from '../utils/get-colored-internal-range-data.js';
import { getTreeMapColorPalette } from '../utils/get-tree-map-color-palette.js';

interface TreeMapDataProviderProps extends PropsWithChildren {
  data: InternalTreeMapNode;
}

export const TreeMapInternalDataProvider = (
  props: TreeMapDataProviderProps,
) => {
  const { data, children } = props;
  const colorPalette = useColorPalette();
  const setState = useSetState();
  const nameAccessor = useNameAccessor();
  const { unit } = useTreeMapRawData();
  const minMaxConfig = useMinMaxConfig();

  const isRangeLegend = nameAccessor === NAME_ACCESSOR.Value;

  const { rangeColorPalette, categoricalColorPalette } =
    getTreeMapColorPalette(colorPalette);

  const { data: internalData, legendColorMap } = useMemo(() => {
    if (isRangeLegend) {
      return getColoredInternalRangeData(
        data,
        unit,
        rangeColorPalette,
        minMaxConfig,
      );
    }

    return getColoredInternalCategoricalData(
      data,
      unit,
      categoricalColorPalette,
      nameAccessor,
    );
  }, [
    isRangeLegend,
    data,
    unit,
    categoricalColorPalette,
    nameAccessor,
    rangeColorPalette,
    minMaxConfig,
  ]);

  setState((prev) => ({
    ...prev,
    colorMap: legendColorMap,
  }));

  return (
    <TreeMapInternalDataContext.Provider value={internalData}>
      {children}
    </TreeMapInternalDataContext.Provider>
  );
};

TreeMapInternalDataProvider['displayName'] = 'TreeMapInternalDataProvider';
